// The only place the app initializes Firebase. Everything else goes through
// the data layer. Auth loads at start; Firestore and the Realtime Database
// load on first use so the first screen downloads less.
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import type { Database } from 'firebase/database';
import type { Firestore } from 'firebase/firestore';

import type { EnvironmentConfig } from '../../config/env.ts';

export type FirebaseServices = {
  app: FirebaseApp;
  auth: Auth;
  emulated: boolean;
  firestore: () => Promise<Firestore>;
  database: () => Promise<Database>;
};

function once<T>(load: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | null = null;
  return () => (pending ??= load());
}

export function initializeFirebase(config: EnvironmentConfig): FirebaseServices {
  const app = initializeApp(config.firebase);
  const auth = getAuth(app);
  if (config.emulators) connectAuthEmulator(auth, config.emulators.auth, { disableWarnings: true });
  return {
    app,
    auth,
    emulated: Boolean(config.emulators),
    firestore: once(async () => {
      const { connectFirestoreEmulator, getFirestore } = await import('firebase/firestore');
      const firestore = getFirestore(app);
      if (config.emulators) connectFirestoreEmulator(firestore, config.emulators.firestore.host, config.emulators.firestore.port);
      return firestore;
    }),
    database: once(async () => {
      const { connectDatabaseEmulator, getDatabase } = await import('firebase/database');
      const database = getDatabase(app);
      if (config.emulators) connectDatabaseEmulator(database, config.emulators.database.host, config.emulators.database.port);
      return database;
    }),
  };
}
